/**
 * The domain API, on top of the shared authenticated fetch.
 *
 * Every screen used to render a `SEED_*` constant declared in its own file, so the app
 * looked complete and stored nothing. These are the calls that replace those constants.
 * One module, so a screen never hand-rolls a fetch and forgets the bearer token.
 */
import { createApiClient } from '../auth/client.js';

const { api } = createApiClient({ baseUrl: '/api' });

export const freight = {
  /* carrier */
  getCarrier: () => api.get('/carrier').then((r) => r?.carrier ?? null),
  saveCarrier: (c) => api.post('/carrier', c).then((r) => r.carrier),

  /* the carrier's own pages (docs/freight-carrier-pages-plan-2026-10.md) */
  /** Home's numbers: { hiring: { ready, working }, dispatch: { needsYou, loads } }, each only if theirs to see. */
  today: () => api.get('/carrier/today'),
  /** Needs you, newest first (backend desk.mjs). */
  desk: () => api.get('/carrier/desk').then((r) => r.items || []),
  /** Mark one call item handled; answers the desk as it now stands. */
  markHandled: (itemId) => api.post('/carrier/desk/handled', { itemId }).then((r) => r.items || []),

  /** Hiring's groups (backend recruiting.mjs hiringBoard): each { count, rows }, newest first. */
  hiring: () => api.get('/carrier/hiring'),
  /** One applicant's page: { applicant, criteria, calls, onboardingItems }. */
  applicant: (id) => api.get(`/carrier/applicants/${encodeURIComponent(id)}`),

  /* the Dispatch pages (backend dispatchPages.mjs), narrowed by the server to "only their own" */
  /** Drivers, latest activity first: each with currentLoad and lastCall. */
  dispatchDrivers: () => api.get('/carrier/dispatch/drivers').then((r) => r.drivers || []),
  /** Open loads (or finished ones), latest activity first: each with driverName and lastCall. */
  dispatchLoads: (closed) => api.get(`/carrier/dispatch/loads${closed ? '?closed=1' : ''}`).then((r) => r.loads || []),
  /** One driver's page: { driver, loads, calls }. */
  dispatchDriver: (id) => api.get(`/carrier/dispatch/drivers/${encodeURIComponent(id)}`),
  /** One load's page: { load, driver, calls, notes, checkCalls }. */
  dispatchLoad: (id) => api.get(`/carrier/dispatch/loads/${encodeURIComponent(id)}`),
  /** A team note on a load. Answers the notes as they now stand. */
  addLoadNote: (id, body) => api.post(`/carrier/dispatch/loads/${encodeURIComponent(id)}/notes`, { body }).then((r) => r.notes || []),
  /** What this person touched lately: [{ kind, id, name, what, at }]. */
  recent: () => api.get('/carrier/dispatch/recent').then((r) => r.recent || []),
  /** { drivers, loads } matching a name, a number, a truck or a trailer. */
  searchDispatch: (q) => api.get(`/carrier/dispatch/search?q=${encodeURIComponent(q)}`),

  /* Reports (backend reports.mjs), admins only */
  /** { tiles, today, feedback } for whole days from–to (the carrier's), optionally between start–end each day. */
  callReport: ({ from, to, start, end }) => api.get(`/carrier/reports/calls?${new URLSearchParams(Object.entries({ from, to, start, end }).filter(([, v]) => v)).toString()}`),
  /** { inputs, rows, total, planPriceCents } for this_month | last_month | 90. */
  savings: (period) => api.get(`/carrier/reports/savings?period=${encodeURIComponent(period)}`),
  saveSavingsInputs: (inputs) => api.put('/carrier/reports/savings/inputs', inputs).then((r) => r.inputs),
  /** { tiles, people } for this_month | last_month | 12m. */
  dncReport: (period) => api.get(`/carrier/reports/dnc?period=${encodeURIComponent(period)}`),
  /** The same, as the CSV text the page saves. */
  dncReportCsv: (period) => api.get(`/carrier/reports/dnc.csv?period=${encodeURIComponent(period)}`),

  /* Rules (backend rules.mjs): { rules, alerts } — alerts are the signed-in admin's own, else null */
  rules: () => api.get('/carrier/rules'),
  saveRules: (patch) => api.put('/carrier/rules', patch).then((r) => r.rules),
  saveAlerts: (patch) => api.put('/carrier/alerts', patch).then((r) => r.alerts),

  /* People in the loop (backend loop.mjs): { dispatch, hiring, recruiter, live }. Every change answers the board. */
  loop: () => api.get('/carrier/loop'),
  addLoopPerson: (p) => api.post('/carrier/loop', p),
  updateLoopPerson: (id, patch) => api.patch(`/carrier/loop/${id}`, patch),
  removeLoopPerson: (id) => api.del(`/carrier/loop/${id}`),
  moveLoopPerson: (id, dir) => api.post(`/carrier/loop/${id}/move`, { dir }),

  /* Admins and access (backend access.mjs), admins only. Every change answers the whole board. */
  /** { people, drivers, loads, notAssigned, scoping } */
  people: () => api.get('/carrier/people'),
  /** { name, email, phone, role: admin|dispatcher|recruiter, sees: all|own, canChangeRules } → { member, invite, ...board } */
  addPerson: (p) => api.post('/carrier/people', p),
  updatePerson: (id, patch) => api.patch(`/carrier/people/${id}`, patch),
  invitePerson: (id) => api.post(`/carrier/people/${id}/invite`, {}),
  /** Exactly these drivers, afterwards. */
  setPersonDrivers: (id, driverIds) => api.put(`/carrier/people/${id}/drivers`, { driverIds }),
  /** A load to one person, or back to its driver's dispatcher (null). */
  setLoadDispatcher: (loadId, dispatcherId) => api.put(`/carrier/loads/${loadId}/dispatcher`, { dispatcherId }),

  /** MC/DOT → FMCSA. `configured:false` means nobody has connected the lookup yet. */
  lookup: ({ mc, dot }) =>
    api.get(`/fmcsa/lookup?${mc ? `mc=${encodeURIComponent(mc)}` : `dot=${encodeURIComponent(dot)}`}`),

  /**
   * The caller-id lines the voice platform will actually dial from. `configured:false`
   * means nobody has connected the platform yet. An empty list is a real answer — the
   * screen says so rather than offering a number that does not exist.
   */
  listLines: () => api.get('/carrier/lines'),

  /* drivers */
  listDrivers: () => api.get('/carrier/drivers').then((r) => r.drivers),
  saveDriver: (d) => api.post('/carrier/drivers', d).then((r) => r.driver),
  // Merges onto the stored row server-side and changes a number IN PLACE, so the driver keeps
  // its loads and history. 409 phone_taken when another driver, removed ones included, has it.
  updateDriver: (id, d) => api.patch(`/carrier/drivers/${id}`, d).then((r) => r.driver),
  removeDriver: (id) => api.del(`/carrier/drivers/${id}`),

  /**
   * Two passes on purpose. `preview` shows what the file actually contains, including a
   * line-numbered reason for every row that will NOT import; `apply` writes them. The
   * screen this replaces showed three fictional drivers whatever you uploaded.
   */
  previewDrivers: (csv) => api.post('/carrier/drivers/import', { csv }),
  importDrivers: (csv, consentText) => api.post('/carrier/drivers/import', { csv, apply: true, consentText }),

  /* loads + dispatchers */
  listLoads: () => api.get('/carrier/loads').then((r) => r.loads),
  saveLoad: (l) => api.post('/carrier/loads', l).then((r) => r.load),
  listDispatchers: () => api.get('/carrier/dispatchers').then((r) => r.dispatchers),
  addDispatcher: (d) => api.post('/carrier/dispatchers', d),

  /* brokerage — the broker half, which until 2026-10-07 had a table and no code.
     saveBrokerage is what makes onboarding survive a refresh; before it, the whole
     sign-up lived in a module-level variable that died with the tab. */
  getBrokerage: () => api.get('/broker').then((r) => r?.brokerage ?? null),
  saveBrokerage: (b) => api.post('/broker', b).then((r) => r.brokerage),
  /**
   * The board's deployment config — team, branches, scope, ladder, rules.
   *
   * ⛔ `live` IS NOT PART OF IT. It is a column with its own route, so that an edit to a phone
   * number cannot switch outbound calling on; the backend refuses a patch carrying it.
   */
  saveBoardConfig: (board) => api.post('/broker/config', { board }).then((r) => r.brokerage),
  setBrokerageLive: (live) => api.post('/broker/live', { live }).then((r) => r.brokerage),

  listBrokerCarriers: (status) => api.get(`/broker/carriers${status ? `?status=${encodeURIComponent(status)}` : ''}`).then((r) => r.carriers || []),
  saveBrokerCarrier: (c) => api.post('/broker/carriers', c).then((r) => r.carrier),
  /**
   * approve | block | prospect. The only way a carrier's status changes.
   *
   * ⛔ THE REASON TRAVELS WITH IT. The screen requires one and tells the operator it goes in
   * the audit trail; this used to post `{ status }` alone, so the justification for a
   * permanent do-not-use record never left the browser. The backend stores it on the row with
   * the author taken from the session.
   */
  setBrokerCarrierStatus: (id, status, reason) =>
    api.post(`/broker/carriers/${id}/status`, { status, reason }).then((r) => r.carrier),

  /**
   * Every status decision ever recorded for one carrier, newest first.
   *
   * ⛔ THE ONLY PLACE AN EARLIER BLOCK'S JUSTIFICATION SURVIVES. The three `status_*` columns
   * on the carrier hold the CURRENT decision, so a carrier blocked for double-brokering in
   * March and approved in June shows only June's reason — and March's is the one a brokerage
   * needs when deciding whether to trust them again. The table and the route existed from the
   * start; nothing had ever called it.
   */
  listBrokerCarrierStatusEvents: (id) =>
    api.get(`/broker/carriers/${id}/status-events`)
      // ⛔ AND WHETHER ANYTHING WAS CUT. Newest-first with a cap, so a capped list is missing
      // the OLDEST decisions — the ones this table exists to keep. The screen says so rather
      // than presenting a truncated list as the whole record.
      .then((r) => ({ events: r.events || [], truncated: r.truncated === true })),

  listBrokerCustomers: () => api.get('/broker/customers').then((r) => r.customers || []),
  saveBrokerCustomer: (c) => api.post('/broker/customers', c).then((r) => r.customer),

  listBrokerLoads: (status) => api.get(`/broker/loads${status ? `?status=${encodeURIComponent(status)}` : ''}`).then((r) => r.loads || []),
  /** ⛔ 409 carrier_blocked when the load names a carrier the brokerage has blocked —
      the error carries the carrier's NAME so the screen can say which one. */
  saveBrokerLoad: (l) => api.post('/broker/loads', l).then((r) => r.load),

  /* calls — answers 501 until the voice-platform seam exists; the UI says so */
  callDriver: (id, body) => api.post(`/carrier/drivers/${id}/call`, body),
  /** Call later (backend scheduled.mjs): { reason, loadId, message, dueAt } → { scheduled }. */
  scheduleCall: (id, body) => api.post(`/carrier/drivers/${id}/schedule`, body).then((r) => r.scheduled),
  cancelScheduled: (id) => api.del(`/carrier/scheduled/${id}`).then((r) => r.scheduled),

  /**
   * The carrier's own call history, plus whether the dial seam is connected at all.
   *
   * Returns `{ calls, configured }`. `configured:false` means nobody has set CORE_URL and
   * CORE_FREIGHT_API_KEY yet — the board says so rather than offering a Call button that
   * writes a row and then fails. Every row is here, including the ones that never
   * connected: the backend writes the row BEFORE it dials, so a refusal nobody read is
   * still visible to a dispatcher.
   */
  listCalls: (limit) => api.get(`/carrier/calls${limit ? `?limit=${encodeURIComponent(limit)}` : ''}`)
    .then((r) => ({ calls: r.calls || [], configured: r.configured !== false })),

  /**
   * Re-read one call from the voice platform. This is how a call stops being "placed" and
   * becomes "completed" with a transcript — nothing pushes into this app, so the screen asks.
   */
  getCall: (id) => api.get(`/carrier/calls/${encodeURIComponent(id)}`).then((r) => r.call || null),

  /**
   * A listen-only pass to a LIVE call to an AI persona (backend/src/persona.mjs): { url, token }.
   * Persona calls only — the platform refuses any other. Throws 409 `no_room` until the voice
   * server has the call (ask again in a second) and 409 `call_ended` once it is over.
   */
  listenCall: (id) => api.post(`/carrier/calls/${encodeURIComponent(id)}/listen`, {}),

  /* recruiting (2026-10-25) — a carrier's job applicants, and Alfred calling them */
  /** { settings, onboardingItems, importConsentText }. The application link is made on first open. */
  getRecruiting: () => api.get('/carrier/recruiting'),
  /** Merges server-side: send only what changed. A recruiter's phone registers them for live transfer. */
  saveRecruiting: (patch) => api.put('/carrier/recruiting', patch).then((r) => r.settings),
  /** Replace a leaked application link; the old one stops working at once. */
  newApplyLink: () => api.post('/carrier/recruiting/new-link', {}).then((r) => r.settings),
  listApplicants: () => api.get('/carrier/applicants').then((r) => r.applicants || []),
  /** Same two passes as the drivers import. `attest` is the recruiter's tick; the server stores its own words for it. */
  previewApplicants: (csv) => api.post('/carrier/applicants/import', { csv }),
  importApplicants: (csv, attest) => api.post('/carrier/applicants/import', { csv, apply: true, attest: attest === true }),
  updateApplicant: (id, patch) => api.patch(`/carrier/applicants/${id}`, patch).then((r) => r.applicant),
  callApplicant: (id, reason) => api.post(`/carrier/applicants/${id}/call`, reason ? { reason } : {}),
  listApplicantCalls: (id) => api.get(`/carrier/applicants/${id}/calls`).then((r) => r.calls || []),
  getApplicantCall: (id) => api.get(`/carrier/applicant-calls/${encodeURIComponent(id)}`).then((r) => r.call || null),
  /** The same listen-only pass, for a call to an AI persona applicant. */
  listenApplicantCall: (id) => api.post(`/carrier/applicant-calls/${encodeURIComponent(id)}/listen`, {}),
  hireApplicant: (id) => api.post(`/carrier/applicants/${id}/hire`, {}),
  setOnboarding: (id, item, done) => api.patch(`/carrier/applicants/${id}/onboarding`, { item, done }).then((r) => r.applicant),

  /* the opt-in page (backend optin.mjs) — no sign-in */
  optInPage: (token) => api.get(`/opt-in/${encodeURIComponent(token)}`),
  submitOptIn: (token, body) => api.post(`/opt-in/${encodeURIComponent(token)}`, body),
  /** The carrier's opt-in link to share: { url } (null when the server has no web address). Admins. */
  optInLink: () => api.get('/carrier/opt-in-link').then((r) => r.url || null),
  /** A new link; the old one stops working at once. Admins. */
  newOptInLink: () => api.post('/carrier/opt-in-link/new', {}).then((r) => r.url || null),
  /** Email the link to one person on a list: { sent, error }. */
  sendOptIn: (loopId) => api.post(`/carrier/loop/${loopId}/send-opt-in`, {}),
  /** { plan, priceCents, billingEmail, processor, card, invoices }. Admins. */
  billing: () => api.get('/carrier/billing').then((r) => r.billing),

  /* the public application page — no sign-in */
  getApplication: (token) => api.get(`/apply/${encodeURIComponent(token)}`),
  submitApplication: (token, form) => api.post(`/apply/${encodeURIComponent(token)}`, form),

  /* who is signed in: { email, staff, via } — via is 'allowlist' (us, brokers, the demo) or 'member' */
  me: () => api.get('/me'),

  /* our admin (MuleWorks staff). Every route is /api/staff/*, refused 403 staff_only for anyone else. */
  staff: {
    listCarriers: () => api.get('/staff/carriers').then((r) => r.carriers || []),
    getCarrier: (id) => api.get(`/staff/carriers/${id}`).then((r) => r.carrier),
    addCarrier: (c) => api.post('/staff/carriers', c).then((r) => r.carrier),
    saveDetails: (id, d) => api.patch(`/staff/carriers/${id}`, d).then((r) => r.carrier),
    /** The platform's own lines: the caller ID is picked from these, never typed. */
    lines: () => api.get('/staff/lines'),
    setCallerId: (id, phone) => api.put(`/staff/carriers/${id}/caller-id`, { phone }).then((r) => r.carrier),
    lookup: (id) => api.post(`/staff/carriers/${id}/lookup`, {}).then((r) => r.carrier),
    audit: (id) => api.get(`/staff/carriers/${id}/audit`).then((r) => r.audit || []),

    catalog: () => api.get('/staff/catalog').then((r) => r.sections || []),
    setup: (id) => api.get(`/staff/carriers/${id}/setup`).then((r) => r.setup),
    saveAnswer: (id, questionId, a) => api.put(`/staff/carriers/${id}/setup/${encodeURIComponent(questionId)}`, a).then((r) => r.setup),

    members: (id) => api.get(`/staff/carriers/${id}/members`).then((r) => r.members || []),
    addMember: (id, m) => api.post(`/staff/carriers/${id}/members`, m).then((r) => r.member),
    updateMember: (id, mid, m) => api.patch(`/staff/carriers/${id}/members/${mid}`, m).then((r) => r.member),
    removeMember: (id, mid) => api.del(`/staff/carriers/${id}/members/${mid}`),
    invite: (id, memberIds) => api.post(`/staff/carriers/${id}/invites`, { memberIds }).then((r) => r.invites || []),

    drivers: (id) => api.get(`/staff/carriers/${id}/drivers`).then((r) => r.drivers || []),
    previewDrivers: (id, csv) => api.post(`/staff/carriers/${id}/drivers/import`, { csv }),
    importDrivers: (id, csv, consent) => api.post(`/staff/carriers/${id}/drivers/import`, { csv, apply: true, consent }),
    previewApplicants: (id, csv) => api.post(`/staff/carriers/${id}/applicants/import`, { csv }),
    importApplicants: (id, csv, consent) => api.post(`/staff/carriers/${id}/applicants/import`, { csv, apply: true, consent }),
    markTestPhone: (id, kind, rowId, on) => api.patch(`/staff/carriers/${id}/${kind}/${rowId}/test-phone`, { testPhone: on }),
    recruiting: (id) => api.get(`/staff/carriers/${id}/recruiting`).then((r) => r.settings),
    optInLink: (id) => api.get(`/staff/carriers/${id}/opt-in-link`).then((r) => r.url || null),
    newOptInLink: (id) => api.post(`/staff/carriers/${id}/opt-in-link/new`, {}).then((r) => r.url || null),
    billing: (id) => api.get(`/staff/carriers/${id}/billing`).then((r) => r.billing),
    saveBilling: (id, b) => api.put(`/staff/carriers/${id}/billing`, b).then((r) => r.billing),
    saveRecruiting: (id, patch) => api.put(`/staff/carriers/${id}/recruiting`, patch).then((r) => r.settings),

    template: () => api.get('/staff/setup-email/template').then((r) => r.template),
    saveTemplate: (t) => api.put('/staff/setup-email/template', t).then((r) => r.template),
    preview: (id, kind) => api.get(`/staff/carriers/${id}/setup-email/preview?kind=${encodeURIComponent(kind || 'full')}`).then((r) => r.preview),
    sendTest: (kind, id) => api.post('/staff/setup-email/test', { kind, carrierId: id || null }).then((r) => r.email),
    sendSetupEmail: (id, kind) => api.post(`/staff/carriers/${id}/setup-email`, { kind }).then((r) => r.email),
    emails: (id) => api.get(`/staff/carriers/${id}/setup-emails`).then((r) => r.emails || []),

    gates: (id) => api.get(`/staff/carriers/${id}/gates`).then((r) => r.gates),
    tick: (id, check, done) => api.put(`/staff/carriers/${id}/checks/${encodeURIComponent(check)}`, { done }).then((r) => r.gates),
    moveTo: (id, stage, extra) => api.post(`/staff/carriers/${id}/stage`, { stage, ...extra }),
  }
};
